import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, Col, Form, Input, Label, Row } from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import ButtonIcon from "../common/ButtonIcon";
import { faUndo } from "@fortawesome/free-solid-svg-icons";
import { PreloadState } from "../UI/preloading/PreloadState";
import Datetime from "react-datetime";
import moment from "moment";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import "moment/locale/es";

const CreatePeriodForm = ({
  period,
  handleSubmitData,
  setRedirect,
  setRedirectUrl,
  handleClose,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
  });

  const [formDate, setformDate] = useState({
    since: null,
    to: null,
  });

  const [discountDueDate, setDiscountDueDate] = useState(null);
  // Día del mes escrito por el usuario, tal cual, y el aviso si no sirve.
  const [discountDueDay, setDiscountDueDay] = useState("");
  const [discountDueDateText, setDiscountDueDateText] = useState("");
  // El porcentaje se lleva en estado local por lo mismo que el día: el input
  // no está registrado en react-hook-form, y setValue sobre un campo sin
  // registrar no re-renderiza, con lo que lo que se escribía no se veía.
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountPercentText, setDiscountPercentText] = useState("");
  const [isCustomPeriod, setIsCustomPeriod] = useState(false);
  const history = useHistory();
  moment.locale("es");

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  const currentMonth = moment().month();
  const year = moment().year();

  useEffect(() => {
    if (period) {
      if (period.name) {
        setValue("name", period.name);
        
        const isPredefined = monthNames.some(month => 
          period.name.includes(`${month} ${year}`)
        );
        
        if (!isPredefined) {
          setIsCustomPeriod(true);
        }
      }

      if (period.startAt) {
        const startDate = moment(period.startAt);
        setValue("start_at", startDate.toDate());
        setformDate(prev => ({...prev, since: startDate}));
      }
      if (period.endAt) {
        const endDate = moment(period.endAt);
        setValue("end_at", endDate.toDate());
        setformDate(prev => ({...prev, to: endDate}));
      }
      if (period.discountPercentage != null) {
        setValue("discount_percentage", period.discountPercentage);
        setDiscountPercent(String(period.discountPercentage));
      } else {
        setValue("discount_percentage", "");
        setDiscountPercent("");
      }
      if (period.discountDueDate) {
        const dueDate = moment(period.discountDueDate);
        setValue("discount_due_date", dueDate.toDate());
        setDiscountDueDate(dueDate);
        // El campo es el día del mes, así que se muestra ese y no la fecha.
        setDiscountDueDay(String(dueDate.date()));
      }
    } else {
      setIsCustomPeriod(false);
      const currentMonthName = monthNames[currentMonth];
      setValue("name", `${currentMonthName} ${year}`);
      
      const start = moment().month(currentMonth).startOf("month");
      const end = moment().month(currentMonth).endOf("month");
      
      setValue("start_at", start.toDate());
      setValue("end_at", end.toDate());
      setformDate({ since: start, to: end });
    }
  }, [period]);

  const handlePeriodTypeChange = (e) => {
    const isCustom = e.target.value === "custom";
    setIsCustomPeriod(isCustom);
    
    if (!isCustom) {
      const currentMonthName = monthNames[currentMonth];
      const monthValue = `${currentMonthName} ${year}`;
      setValue("name", monthValue);

      const start = moment().month(currentMonth).startOf("month");
      const end = moment().month(currentMonth).endOf("month");
      
      setValue("start_at", start.toDate());
      setValue("end_at", end.toDate());
      setformDate({ since: start, to: end });
    } else {
      setValue("name", "");
    }
  };

  const handleNameChange = (e) => {
    setValue("name", e.target.value, { shouldValidate: true });
  };

  const handleStartDateChange = (d) => {
    setformDate((prev) => ({
      ...prev,
      since: d,
    }));
    setValue("start_at", d.toDate());
  };

  const handleEndDateChange = (d) => {
    setformDate((prev) => ({ ...prev, to: d }));
    setValue("end_at", d.toDate());
  };

  // Inicio del período: el día se convierte a fecha dentro de este mes.
  const periodStart =
    formDate.since || (watch("start_at") ? moment(watch("start_at")) : null);

  // El pronto pago premia pagar temprano, asi que la fecha limite va en la
  // primera parte del mes. Mas alla del 20 dejaria de ser "pronto pago" y se
  // solaparia con el cierre del periodo.
  const MAX_DISCOUNT_DAY = 20;
  const maxDiscountDay = MAX_DISCOUNT_DAY;

  // Mes del período, como clave estable para el efecto de abajo: periodStart es
  // un moment nuevo en cada render y no sirve como dependencia.
  const periodStartKey = periodStart ? periodStart.format("YYYY-MM") : null;

  // Al cambiar el mes hay que rearmar la fecha límite con el día ya escrito.
  // Si no, quedaba la del mes anterior: se elegía el día 10 en Septiembre, se
  // cambiaba a Junio y el período seguía diciendo "vence el 10/09".
  useEffect(() => {
    if (!discountDueDay || !periodStartKey || !periodStart) return;

    const day = Number(discountDueDay);
    if (!Number.isInteger(day) || day < 1 || day > MAX_DISCOUNT_DAY) return;

    const derived = periodStart.clone().date(day);
    setDiscountDueDate(derived);
    setValue("discount_due_date", derived.toDate());
    // eslint-disable-next-line
  }, [periodStartKey, discountDueDay]);

  // El atributo max del input no impide escribir 1000: HTML solo lo valida en
  // el submit nativo, que aca no ocurre. Se avisa al escribir y se frena el
  // guardado, en linea con lo que exige el backend (min 1, max 100).
  const handleDiscountPercentChange = (raw) => {
    const text = String(raw ?? "").trim();

    setDiscountPercent(text);

    if (!text) {
      setValue("discount_percentage", "", { shouldValidate: true });
      setDiscountPercentText("");
      return;
    }

    const pct = Number(text);

    if (Number.isNaN(pct) || pct < 1 || pct > 100) {
      setValue("discount_percentage", "", { shouldValidate: true });
      setDiscountPercentText("El descuento debe estar entre 1 y 100.");
      return;
    }

    setValue("discount_percentage", text, { shouldValidate: true });
    setDiscountPercentText("");
  };

  const handleDiscountDueDayChange = (raw) => {
    const text = String(raw ?? "").trim();

    if (!text) {
      setDiscountDueDay("");
      setDiscountDueDate(null);
      setValue("discount_due_date", null);
      setDiscountDueDateText("");
      return;
    }

    setDiscountDueDay(text);

    const day = Number(text);

    if (!periodStart) {
      setDiscountDueDate(null);
      setValue("discount_due_date", null);
      setDiscountDueDateText("Elige primero el mes del período.");
      return;
    }

    if (!Number.isInteger(day) || day < 1 || day > MAX_DISCOUNT_DAY) {
      // Se avisa en vez de guardar null en silencio: el período quedaría con
      // porcentaje y sin fecha límite, y el descuento no se aplicaría nunca.
      setDiscountDueDate(null);
      setValue("discount_due_date", null);
      setDiscountDueDateText(
        `Escribe un día entre 1 y ${MAX_DISCOUNT_DAY}.`
      );
      return;
    }

    const derived = periodStart.clone().date(day);
    setDiscountDueDate(derived);
    setValue("discount_due_date", derived.toDate());
    setDiscountDueDateText("");
  };

  // Con texto suelto en la fecha límite no se guarda: el período quedaría con
  // porcentaje y sin fecha, y el backend no aplica el descuento sin las dos
  // cosas. El aviso ya está bajo el campo.
  const guardAgainstLooseDiscountDate = (values) => {
    if (discountDueDateText || discountPercentText) {
      return;
    }

    return handleSubmitData(values);
  };

  const renderForm = () => (
    <>
      <FalconCardHeader title="Detalle del período" light={false} />
      <CardBody>
        <Row className="mb-4">
          <Col sm={6} className="mb-3 mb-sm-0">
            <Label className="mb-0">Tipo de período</Label>
            <Input
              type="select"
              name="periodType"
              onChange={handlePeriodTypeChange}
              value={isCustomPeriod ? "custom" : "predefined"}
            >
              <option value="predefined">Mensual (predeterminado)</option>
              <option value="custom">Acumulado/Personalizado</option>
            </Input>
          </Col>
          
          {!isCustomPeriod ? (
            <Col sm={6} className="mb-3 mb-sm-0">
              <Label className="mb-0">Seleccionar mes</Label>
              <Input
                type="select"
                name="name"
                {...register("name", {
                  required: "Seleccione un período",
                })}
                value={watch("name") || ""}
                onChange={(e) => {
                  const monthIndex = monthNames.indexOf(e.target.value.split(" ")[0]);
                  if (monthIndex !== -1) {
                    const start = moment().year(year).month(monthIndex).startOf("month");
                    const end = moment().year(year).month(monthIndex).endOf("month");
                    
                    setValue("start_at", start.toDate());
                    setValue("end_at", end.toDate());
                    setformDate({ since: start, to: end });
                  }
                  setValue("name", e.target.value);
                }}
              >
                <option disabled value="">
                  Seleccione un mes
                </option>
                {monthNames.slice(0, currentMonth + 1).map((month, index) => (
                  <option key={index} value={`${month} ${year}`}>
                    {`${month} ${year}`}
                  </option>
                ))}
              </Input>
              {errors.name && (
                <WizardError
                  error={errors.name.message}
                  className="fs--1 font-weight-normal d-block mt-2"
                />
              )}
            </Col>
          ) : (
            <Col sm={6} className="mb-3 mb-sm-0">
              <Label className="mb-0">Nombre personalizado</Label>
              <Input
                type="text"
                placeholder="Ej: Acumulado Enero-Marzo 2024"
                value={watch("name") || ""}
                onChange={handleNameChange}
              />
              {errors.name && (
                <WizardError
                  error={errors.name.message}
                  className="fs--1 font-weight-normal d-block mt-2"
                />
              )}
            </Col>
          )}
        </Row>

        <Row className="mb-4">
          <Col sm={6} className="mb-3 mb-sm-0">
            <Label className="mb-0">Desde</Label>
            <Datetime
              className="w-100"
              name="start_at"
              value={watch("start_at")}
              onChange={handleStartDateChange}
              dateFormat="DD/MM/YYYY"
              timeFormat={false}
              locale="es"
              inputProps={{
                ...register("start_at", {
                  required: "Es obligatorio",
                })
              }}
            />
            {errors.start_at && (
              <WizardError
                error={errors.start_at.message}
                className="fs--1 font-weight-normal d-block mt-2"
              />
            )}
          </Col>

          <Col sm={6} className="mb-3 mb-sm-0">
            <Label className="mb-0">Hasta</Label>
            <Datetime
              className="w-100"
              name="end_at"
              value={watch("end_at")}
              isValidDate={(current) => {
                return formDate.since ? current.isAfter(formDate.since) : true;
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat={false}
              onChange={handleEndDateChange}
              locale="es"
              inputProps={{
                ...register("end_at", {
                  required: "Es obligatorio",
                })
              }}
            />
            {errors.end_at && (
              <WizardError
                error={errors.end_at.message}
                className="fs--1 font-weight-normal d-block mt-2"
              />
            )}
          </Col>
        </Row>

        <Row className="mb-4">
          <Col sm={6} className="mb-3 mb-sm-0">
            <Label className="mb-0">
              % de descuento por pronto pago{" "}
              <small className="text-muted">(opcional)</small>
            </Label>
            <Input
              type="number"
              name="discount_percentage"
              min={1}
              max={100}
              step={0.01}
              placeholder="Usa el default del condominio si se deja vacío"
              value={discountPercent}
              onChange={(e) => handleDiscountPercentChange(e.target.value)}
            />
            {discountPercentText ? (
              <WizardError
                error={discountPercentText}
                className="fs--1 font-weight-normal d-block mt-2"
              />
            ) : (
              errors.discount_percentage && (
                <WizardError
                  error={errors.discount_percentage.message}
                  className="fs--1 font-weight-normal d-block mt-2"
                />
              )
            )}
          </Col>

          <Col sm={6} className="mb-3 mb-sm-0">
            <Label className="mb-0">
              Día límite de pronto pago{" "}
              <small className="text-muted">(opcional; día 1 al 20)</small>
            </Label>
            {/* Un dia del mes y no un calendario: el datepicker dejaba elegir
                una fecha de otro mes —el periodo era Junio y ofrecia
                Septiembre— y guardaba una fecha limite fuera del periodo. Un
                dia siempre cae dentro, y es la misma convencion que usa la
                configuracion del condominio ("Dia limite de pronto pago"). */}
            <Input
              type="number"
              name="discount_due_day"
              min={1}
              max={maxDiscountDay}
              step={1}
              placeholder="Ej: 10"
              value={discountDueDay}
              onChange={(e) => handleDiscountDueDayChange(e.target.value)}
            />
            {discountDueDateText ? (
              <WizardError
                error={discountDueDateText}
                className="fs--1 font-weight-normal d-block mt-2"
              />
            ) : discountDueDate ? (
              <small className="text-muted d-block mt-1 fs--1">
                Vence el {discountDueDate.format("DD/MM/YYYY")}.
              </small>
            ) : (
              <small className="text-muted d-block mt-1 fs--1">
                Si lo dejas vacío se usa el día configurado en el condominio.
              </small>
            )}
          </Col>
        </Row>

        <Row>
          <Col className="d-flex justify-content-end">
            <ButtonIcon
              color="falcon-default"
              className="ml-auto px-5 mr-2"
              icon={faUndo}
              onClick={() => history.push({ pathname: `/dashboard/periods` })}
              iconAlign="right"
              transform="down-1 shrink-4"
            >
              Volver
            </ButtonIcon>
            <ButtonIcon
              data-testid="submit-button"
              color="primary"
              className="px-5 ml-2"
              type="submit"
              icon="save"
              iconAlign="right"
              transform="down-1 shrink-4"
            >
              Guardar
            </ButtonIcon>
          </Col>
        </Row>
      </CardBody>
    </>
  );

  return (
    <Card
      data-testid="form"
      tag={Form}
      onSubmit={handleSubmit(guardAgainstLooseDiscountDate)}
      className="bg-light"
    >
      <PreloadState loading={isSubmitting} message="Guardando período...">
        {renderForm()}
      </PreloadState>
    </Card>
  );
};

export default CreatePeriodForm;